<template>
  <v-app>
    <div v-if="!state.authRoute && getCurrentRoute !== 'NoAccess'">
      <Appbar
        @toggleNav="toggleNav"
        @openNav="state.navDrawer = true"
        :pageTitle="this.$route.meta.pageTitle"
        :userName="userFullName()"
      />
      <NavSidebar app v-model="state.navDrawer" :navType="xsBreakpoint"></NavSidebar>
    </div>
    <div v-else>
      <v-app-bar density="compact" color="primary" fixed elevation="5">
        <v-app-bar-title>Destination Pet Admin</v-app-bar-title>
      </v-app-bar>
    </div>
    <v-main>
      <v-banner :sticky="true" lines="one" v-if="state.updateExists" color="primary" class="pr-6">
        <template v-slot:text> An update is available. </template>
        <template v-slot:actions>
          <v-btn color="primary" @click="refreshApp"> Update </v-btn>
        </template>
      </v-banner>
      <div id="alert-region"></div>
      <router-view></router-view>
    </v-main>
    <idle-warning />
  </v-app>
</template>

<script lang="ts">
import { reactive, defineComponent, computed } from 'vue'
import Appbar from './components/Appbar.vue'
import NavSidebar from './components/NavSidebar.vue'
import { getUserName } from '@/composables/extractFromJwt'
import router from '@/router'
import IdleWarning from '@/components/IdleWarning.vue'

export default defineComponent({
  name: 'App',
  components: {
    Appbar,
    NavSidebar,
    IdleWarning,
  },

  computed: {
    xsBreakpoint() {
      return this.$vuetify.display.xs
    },
  },

  setup() {
    const state: any = reactive({
      navDrawer: true,
      fullName: '',
      authRoute: true,
      registration: null,
      updateExists: false,
      refreshing: false,
    })

    const getCurrentRoute = computed(() => {
      return router.currentRoute.value.name
    })

    const toggleNav = () => {
      state.navDrawer = !state.navDrawer
    }

    const userFullName = () => {
      const userInfo = getUserName()
      if (!userInfo) return ''

      const { given_name, family_name } = userInfo
      const buildFullName = given_name ? `${given_name} ` : ''
      const fullName = family_name ? `${buildFullName}${family_name}` : buildFullName.trim()
      return fullName
    }

    const userAuthRoute = (route: string): void => {
      switch (route) {
        case 'Login':
          state.authRoute = true
          break
        case 'ForgotPassword':
          state.authRoute = true
          break
        case 'UpdatePasswordWithCode':
          state.authRoute = true
          break
        default:
          state.authRoute = false
      }
    }

    const updateAvailable = (event: any) => {
      state.registration = event.detail
      state.updateExists = true
    }
    const refreshApp = (): void => {
      state.updateExists = false
      // Make sure we only send a 'skip waiting' message if the SW is waiting
      if (!state.registration || !state.registration.waiting) return
      // Send message to SW to skip the waiting and activate the new SW
      state.registration.waiting.postMessage({ type: 'SKIP_WAITING' })
    }

    return {
      state,
      toggleNav,
      userFullName,
      userAuthRoute,
      getCurrentRoute,
      updateAvailable,
      refreshApp,
    }
  },
  mounted() {
    this.$router.afterEach((to: any) => {
      this.userAuthRoute(to.name)
    }),
      document.addEventListener('swUpdated', this.updateAvailable, { once: true })

    // On startup we're occassionally hitting an error where serviceWorker is not available.
    // Defensively skip this if we don't have serviceWorker in navigator.
    if ('serviceWorker' in navigator && navigator.serviceWorker) {
      console.log('Service Worker is supported in this browser.')
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        // We'll also need to add 'refreshing' to our data originally set to false.
        if (this.state.refreshing) return
        this.state.refreshing = true
        // Here the actual reload of the page occurs
        window.location.reload()
      })
    }
  },
})
</script>

<style lang="scss">
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}

body .v-input__details {
  display: none;
}

.v-input.v-input--density-compact {
  --v-input-control-height: 32px;
}

.v-checkbox {
  &.shrink-height {
    .v-selection-control {
      height: unset;
    }
  }
}

div.v-textarea > div.v-field__control > div.v-field__field {
  align-items: flex-start;
  margin-top: 1.5rem;
}

div.v-textarea > div.v-input__details {
  display: inline-flex !important;
  padding-inline-start: 0;
  padding-inline-end: 0;
  padding-top: 0;
  margin-bottom: 0;
  .v-counter {
    display: block !important;
  }
}

.v-input.v-field--active .v-field__field {
  padding-top: 4px;
}

.v-label.v-field-label {
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-style: italic;
}

.v-label.v-field-label--floating {
  font-size: 0.85em;
}

.v-label.v-field-label--floating:empty {
  margin: 0;
}

// App error styles
.error {
  .v-field--variant-outlined .v-field__outline {
    color: rgb(var(--v-theme-error));
  }

  .input-errors {
    color: rgb(var(--v-theme-error));
    font-size: 0.8em;
  }
  .basic-select,
  .dp__input.datepicker-input {
    border-color: rgb(var(--v-theme-error));
  }
}

.dp__input {
  &.datepicker-input {
    border: solid 1px #0005;
    padding-top: 4px;
    padding-bottom: 4px;

    &:hover {
      border: solid 1px #000c;
    }

    &::placeholder {
      font-size: 1rem;
      font-style: italic;
      letter-spacing: 0.009375em;
      color: rgba(var(--v-theme-on-surface), 0.8);
      font-family: Avenir, Helvetica, Arial, sans-serif;
    }
  }
}

// App stepper styles
.dp-stepper {
  margin: 8px 0;
}

.dp-step-group {
  display: flex;
  width: 90%;
}

.dp-step {
  display: flex;
  margin: 16px 12px 16px 64px;
  border-left: 1px solid #ccc;
  min-height: 125px;
}

.dp-step-info {
  display: flex;
  margin: 16px 12px 24px 65px;
  border-left: 1px solid #ccc;
  min-height: 115px;
  width: 40px;
}

.dp-step-action {
  display: flex;
  margin: 16px 12px 16px 65px;
  min-height: 125px;
}

.dp-step-number {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: -20px;
  margin-left: -32px;
  font-size: 12px;
  width: 64px;
  height: 64px;
  border: solid 20px #fff;
  border-radius: 100%;
  background-color: rgb(var(--v-theme-primary));
  color: #fff;
}

.dp-step-valid {
  background-color: rgb(var(--v-theme-success));

  i {
    margin: 0;
  }
}

.dp-step-content {
  width: 100%;
  padding: 16px 16px 16px 0;

  h2 {
    font-weight: normal;
    font-size: 16px;
    margin-bottom: 16px;
  }

  h3 {
    padding-top: 16px;
    padding-bottom: 8px;
  }

  h2 + header h3 {
    padding-top: 0px;
  }

  .dp-step-input {
    max-width: 250px;
    margin-top: 12px;
    margin-bottom: 4px;
  }

  .content-label {
    cursor: default;
  }
}

.basic-select {
  display: inline-block;
  border: solid 1px #bbb;
  margin-right: -20px;
  position: relative;

  &.min {
    select {
      min-width: 30px;
    }
  }

  select {
    font-size: 1em;
    padding: 5px 30px 5px 10px;
    min-width: 250px;
  }

  i {
    pointer-events: none;
    position: absolute;
    right: 0;
    height: 15px;
    width: 15px;
    margin-right: 8px;
    margin-top: 10px;
  }
}

.v-dialog {
  .v-overlay__content {
    min-width: 375px !important;
  }
}

//Timeline custom styling...
.v-timeline-item .v-timeline-item__body {
  grid-column: 3/30 !important;
  padding-inline-start: 8px !important;
}

.v-timeline-item:nth-child(2n) .v-timeline-item__body {
  padding-inline-end: initial !important;
}

.v-timeline-item:nth-child(2n) .v-timeline-item__opposite {
  padding-inline-start: 0 !important;
}

.v-timeline-item .v-timeline-item__opposite,
.v-timeline-item:nth-child(2n + 1) .v-timeline-item__opposite {
  padding-inline-end: 0 !important;
}

.v-timeline-divider__dot--size-x-small .v-timeline-divider__inner-dot {
  height: calc(100% - 4px) !important;
  width: calc(100% - 4px) !important;
}

.v-timeline .v-timeline-divider__dot {
  background: grey !important;
}

.v-timeline .v-timeline-divider__dot--size-x-small {
  height: 18px !important;
  width: 18px !important;
}

.w-5 {
  width: 5%;
}

.w-15 {
  width: 15%;
}

.w-20 {
  width: 20%;
}

.w-35 {
  width: 35%;
}

.input-help-text {
  font-size: 0.8em;
  font-weight: 300;
  color: #4f4f4f;
}
// remove padding when 2 overlay shows
.v-overlay {
  &.v-overlay--scroll-blocked {
    padding-right: 0;
  }
}
</style>
